import { useEffect, useMemo, useState } from "react";
import { fetchStoreLeadMarketCompanyList } from "../../utils/api/reportApi";
import styles from "./MarketViewerV2.module.css";

const nf = new Intl.NumberFormat("en-US");

const valueOf = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const metric = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "string" && value.trim()) return value;
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return nf.format(num);
};

const money = (value, currency = "USD") => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${currency} ${nf.format(Math.round(num))}`;
};

const decimalMoney = (value, currency = "USD") => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${currency} ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)}`;
};

const Card = ({ label, value, small = false }) => (
  <div className={styles.card}>
    <span className={styles.label}>{label}</span>
    <span className={small ? `${styles.value} ${styles.smallValue}` : styles.value}>
      {value}
    </span>
  </div>
);

const CompanyMiniTable = ({ companies = [], title, accent = false }) => {
  if (!Array.isArray(companies) || !companies.length) return null;
  return (
    <div className={styles.miniTableWrap}>
      <div className={accent ? `${styles.miniTableTitle} ${styles.miniTableTitleAccent}` : styles.miniTableTitle}>
        {title}
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Company</th>
              <th>Domain</th>
              <th>Country</th>
              <th>Est. Sales</th>
              <th>Est. Visits</th>
              <th>Products</th>
              <th>Avg Price</th>
              <th>Platform</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c, idx) => (
              <tr key={c?.company_domain || c?.domain || idx}>
                <td className={styles.rankCell}>{c?.rank ?? idx + 1}</td>
                <td>{valueOf(c?.company_name)}</td>
                <td>
                  {c?.domain_url || c?.domain || c?.company_domain ? (
                    <a
                      className={styles.domainLink}
                      href={c?.domain_url || `https://${c?.domain || c?.company_domain}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {c?.domain || c?.company_domain || "—"}
                    </a>
                  ) : "—"}
                </td>
                <td>{valueOf(c?.company_base_country)}</td>
                <td>{c?.estimated_sales_formatted || money(c?.estimated_sales, c?.currency || "USD")}</td>
                <td>{c?.estimated_visits_formatted || metric(c?.estimated_visits)}</td>
                <td>{c?.product_count_formatted || metric(c?.product_count)}</td>
                <td>{c?.average_product_price_formatted || metric(c?.average_product_price)}</td>
                <td>{valueOf(c?.platform)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getTamRows = (data) => data?.tam?.market_sizing?.tam_by_country || [];
const getSamRows = (data) => data?.sam || [];
const getSomRows = (data) => data?.som || [];

const marketKey = (row) =>
  `${row?.market_scope?.selected_country_code || row?.country_code || ""}:${row?.market_scope?.selected_shipping_country || row?.selected_shipping_country || ""}`;

const normalizeCountryCode = (value) => String(value || "").trim().toUpperCase();

const rankMarketCompanies = (companies, field, countryCode = null) => {
  const activeCountryCode = normalizeCountryCode(countryCode);
  return (Array.isArray(companies) ? companies : [])
    .filter((company) => {
      if (!activeCountryCode) return true;
      return normalizeCountryCode(company?.company_base_country) === activeCountryCode;
    })
    .filter((company) => Number.isFinite(Number(company?.[field])))
    .slice()
    .sort((a, b) => Number(b?.[field]) - Number(a?.[field]))
    .map((company, index) => ({ ...company, rank: index + 1 }));
};

const MarketViewerV2 = ({ data = {}, authToken = null }) => {
  const tamRows = getTamRows(data);
  const samRows = getSamRows(data);
  const somRows = getSomRows(data);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [companyMeta, setCompanyMeta] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companyError, setCompanyError] = useState("");

  const markets = useMemo(() => {
    const fromTam = tamRows.map((row) => ({
      country_code: row.country_code,
      selected_shipping_country: row.selected_shipping_country,
    }));
    if (fromTam.length) return fromTam;
    return (data?.market_scope?.selected_shipping_countries || []).map((row) => ({
      country_code: row.country_code,
      selected_shipping_country: row.shipping_country,
    }));
  }, [data, tamRows]);

  const selectedMarket = markets[selectedIndex] || markets[0] || {};
  const selectedKey = `${selectedMarket.country_code || ""}:${selectedMarket.selected_shipping_country || ""}`;
  const tam = tamRows.find((row) => marketKey(row) === selectedKey) || tamRows[selectedIndex] || {};
  const sam =
    samRows.find((row) => marketKey(row) === selectedKey) || samRows[selectedIndex] || {};
  const som =
    somRows.find((row) => marketKey({ market_scope: row?.market_scope }) === selectedKey) ||
    somRows[selectedIndex] ||
    {};
  const samScope = sam?.market_scope || {};
  const somVariant = som?.variants?.[0] || {};
  const somRef =
    somVariant?.company_list_ref ||
    som?.company_list_storage?.refs?.find((ref) => ref?.list_type === somVariant?.key) ||
    som?.company_list_storage?.refs?.[0] ||
    null;
  const runKey = somRef?.run_key || som?.company_list_storage?.run_key || null;
  const listType = somRef?.list_type || somVariant?.key || null;
  const currency =
    som?.own_store_metrics?.currency ||
    som?.shop?.markets?.find((market) => market?.name === selectedMarket.selected_shipping_country)
      ?.baseCurrency ||
    "USD";

  useEffect(() => {
    let cancelled = false;
    setCompanies([]);
    setCompanyMeta(null);
    setCompanyError("");
    if (!runKey || !listType) return undefined;

    setLoadingCompanies(true);
    fetchStoreLeadMarketCompanyList({
      runKey,
      reportType: "SOM",
      listType,
      countryCode: selectedMarket.country_code,
      page: 1,
      limit: 100,
      authToken,
    })
      .then((payload) => {
        if (cancelled) return;
        setCompanies(Array.isArray(payload?.companies) ? payload.companies : []);
        setCompanyMeta(payload || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setCompanyError(err?.message || "Unable to load SOM companies.");
      })
      .finally(() => {
        if (!cancelled) setLoadingCompanies(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authToken, listType, runKey, selectedMarket.country_code]);

  const tamCurrency = currency;
  const samSummary = sam?.market_size?.summary || {};
  const samTypical = sam?.market_size?.typical_company || {};
  const somSummary = somVariant?.market_size?.summary || {};
  const own = som?.own_store_metrics || {};

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Shop Market Analysis V2</h1>
          <p className={styles.subtitle}>
            TAM and SAM show market numbers. SOM shows stores matching the user store price and catalog groups.
          </p>
        </div>
        <div className={styles.badgeRow}>
          {markets.map((market, index) => (
            <button
              type="button"
              key={`${market.country_code || index}:${market.selected_shipping_country}`}
              className={styles.button}
              onClick={() => setSelectedIndex(index)}
              disabled={index === selectedIndex}
            >
              {market.selected_shipping_country || market.country_code}
            </button>
          ))}
        </div>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Market Scope</h2>
        <div className={styles.grid}>
          <Card label="Selected shipping country" value={selectedMarket.selected_shipping_country || "N/A"} />
          <Card label="Selected category" value={samScope.selected_category || data?.market_scope?.selected_category || "N/A"} />
          <Card label="Selected subcategory" value={samScope.selected_subcategory || data?.market_scope?.selected_subcategory || "N/A"} />
          <Card label="Country code" value={selectedMarket.country_code || "N/A"} />
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>TAM Market Size</h2>
        <div className={styles.grid}>
          <Card label="Total companies" value={metric(tam.store_count_formatted || tam.store_count)} />
          <Card label="Estimated sales" value={money(tam.total_estimated_sales_yearly, tamCurrency)} />
          <Card label="Estimated visits" value={metric(tam.total_estimated_visits_formatted || tam.total_estimated_visits)} />
          <Card label="Median sales/company" value={money(tam.median_estimated_sales_yearly, tamCurrency)} />
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>SAM Market Size</h2>
        <div className={styles.grid}>
          <Card label="Total companies" value={metric(samSummary.total_number_of_companies_formatted || sam.company_count)} />
          <Card label="Estimated sales" value={money(samSummary.total_estimated_market_sales, tamCurrency)} />
          <Card label="Estimated visits" value={metric(samSummary.total_estimated_market_visits_formatted || samSummary.total_estimated_market_visits)} />
          <Card label="Median sales/company" value={money(samSummary.median_estimated_sales_per_company, tamCurrency)} />
        </div>
        <div className={styles.grid} style={{ marginTop: 10 }}>
          <Card label="Median visits/company" value={metric(samTypical.median_estimated_visits_per_company_formatted || samTypical.median_estimated_visits_per_company)} />
          <Card label="Median products" value={metric(samTypical.median_number_of_products_formatted || samTypical.median_number_of_products)} />
          <Card label="Median average price" value={decimalMoney(samTypical.median_average_product_price, tamCurrency)} />
          <Card label="Page views" value={metric(samSummary.total_estimated_market_page_views_formatted || samSummary.total_estimated_market_page_views)} />
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>SOM Fit Logic</h2>
        <div className={styles.rules}>
          <Card label="Price rule" value="Same 4-part average product price group as user store" small />
          <Card label="Catalog rule" value="Same 4-part product-count group as user store" small />
          <Card label="Business model rule" value={valueOf(som?.similarity_rules?.business_model_rule_status, "Skipped")} small />
        </div>
        <div className={styles.grid} style={{ marginTop: 10 }}>
          <Card label="User average product price" value={decimalMoney(own.average_product_price, currency)} />
          <Card label="User product count" value={metric(own.product_count_formatted || own.product_count)} />
          <Card label="SOM companies" value={metric(somVariant.company_count_formatted || somVariant.company_count)} />
          <Card label="SOM estimated sales" value={money(somSummary.total_estimated_market_sales, currency)} />
        </div>
      </section>

      {/* SOM Variants — market leaders embedded in report data */}
      {Array.isArray(som?.variants) && som.variants.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>SOM Variants &amp; Market Leaders</h2>
          {som.variants.map((variant) => {
            const vLeaders = variant?.market_size?.market_leaders || {};
            const topSales = vLeaders?.top_companies_by_estimated_sales;
            const topVisits = vLeaders?.top_companies_by_estimated_visits;
            const topSalesSameCountry = rankMarketCompanies(
              vLeaders?.top_companies_by_estimated_sales_same_country,
              "estimated_sales",
              selectedMarket.country_code
            );
            const topVisitsSameCountry = rankMarketCompanies(
              vLeaders?.top_companies_by_estimated_visits_same_country,
              "estimated_visits",
              selectedMarket.country_code
            );
            const vSummary = variant?.market_size?.summary || {};
            const hasSameCountry = Array.isArray(topSalesSameCountry) && topSalesSameCountry.length > 0;
            const hasVisitsSameCountry =
              Array.isArray(topVisitsSameCountry) && topVisitsSameCountry.length > 0;
            const selectedCountryLabel =
              selectedMarket.selected_shipping_country || selectedMarket.country_code || "Active Market";

            return (
              <div key={variant?.key} className={styles.variantBlock}>
                <div className={styles.variantHeader}>
                  <span className={styles.variantName}>{variant?.name || variant?.key}</span>
                  <span className={styles.variantCount}>
                    {metric(variant?.company_count_formatted || variant?.company_count)} companies
                  </span>
                </div>
                {variant?.rule ? (
                  <p className={`${styles.muted} ${styles.variantRule}`}>{variant.rule}</p>
                ) : null}
                <div className={styles.grid} style={{ marginBottom: 12 }}>
                  <Card label="Companies" value={metric(vSummary.total_number_of_companies_formatted || vSummary.total_number_of_companies || variant?.company_count)} />
                  <Card label="Est. market sales" value={money(vSummary.total_estimated_market_sales, currency)} />
                  <Card label="Est. market visits" value={metric(vSummary.total_estimated_market_visits_formatted || vSummary.total_estimated_market_visits)} />
                  <Card label="Median sales/co" value={money(vSummary.median_estimated_sales_per_company, currency)} />
                </div>

                <CompanyMiniTable
                  companies={topSales}
                  title={`Top Companies by Estimated Sales — All Markets (${vLeaders?.top_group_size ?? (topSales?.length ?? 0)} shown)`}
                />

                {hasSameCountry ? (
                  <CompanyMiniTable
                    companies={topSalesSameCountry}
                    title={`Top Companies by Estimated Sales — Same Country as ${selectedCountryLabel} (${topSalesSameCountry.length} shown)`}
                    accent
                  />
                ) : null}

                <CompanyMiniTable
                  companies={topVisits}
                  title={`Top Companies by Estimated Visits — All Markets (${topVisits?.length ?? 0} shown)`}
                />

                {hasVisitsSameCountry ? (
                  <CompanyMiniTable
                    companies={topVisitsSameCountry}
                    title={`Top Companies by Estimated Visits — Same Country as ${selectedCountryLabel} (${topVisitsSameCountry.length} shown)`}
                    accent
                  />
                ) : null}
              </div>
            );
          })}
        </section>
      ) : null}

      <section className={styles.section}>
        <div className={styles.toolbar}>
          <h2 className={styles.sectionTitle} style={{ margin: 0 }}>SOM Companies</h2>
          <span className={styles.muted}>
            {loadingCompanies
              ? "Loading companies..."
              : companyMeta
                ? `${metric(companyMeta.total)} total, showing ${companies.length}`
                : "No company list loaded"}
          </span>
        </div>
        {companyError ? <p className={styles.muted}>{companyError}</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Domain</th>
                <th>Base country</th>
                <th>Price</th>
                <th>Products</th>
                <th>Sales</th>
                <th>Visits</th>
                <th>Platform</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {companies.length ? (
                companies.map((company) => (
                  <tr key={company.domain || company.company_domain}>
                    <td>{valueOf(company.company_name)}</td>
                    <td>
                      {company.domain_url || company.domain ? (
                        <a
                          className={styles.domainLink}
                          href={company.domain_url || `https://${company.domain}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {company.domain || company.company_domain}
                        </a>
                      ) : "N/A"}
                    </td>
                    <td>{valueOf(company.company_base_country)}</td>
                    <td>{decimalMoney(company.average_product_price, company.currency || currency)}</td>
                    <td>{metric(company.product_count_formatted || company.product_count)}</td>
                    <td>{money(company.estimated_sales, company.currency || currency)}</td>
                    <td>{metric(company.estimated_visits_formatted || company.estimated_visits)}</td>
                    <td>{valueOf(company.platform)}</td>
                    <td>{valueOf(company.currency)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className={styles.muted}>
                    {loadingCompanies ? "Loading..." : "No SOM companies available for this market."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default MarketViewerV2;
